import React, { useState } from "react";

const How_to_Authenticate = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [certificate, setCertificate] = useState(null);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [failedResponse, setFailedResponse] = useState(null);

  const [form, setForm] = useState({
    unique_code: "",
    email: "",
    phone: "",
  });

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
    setVerificationFailed(false);
    setFailedResponse(null);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const fetchCertificate = async (batchNumber) => {
    try {
      const res = await fetch(
        "https://evercorenutrition.com/ev_admin/api/certificates/",
      );
      if (!res.ok) return null;
      const data = await res.json();
      const certificates =
        data.results?.data || data.results || data.data || [];
      const match = certificates.find(
        (c) =>
          c.batch_number === batchNumber ||
          c.certificate_number === batchNumber,
      );
      if (match) {
        return {
          id: match.id,
          product_name: match.product_name,
          product_sku: match.product_sku,
          variant_name: match.variant_name,
          variant_weight: match.variant_weight,
          batch_number: match.batch_number,
          image_url: match.image_url,
          created_at: match.created_at,
          is_active: match.is_active,
          certificate_number: match.batch_number,
          title: `Authenticity Certificate - ${match.product_name}`,
          verification_status: match.is_active ? "verified" : "expired",
          verification_status_display: match.is_active ? "Verified" : "Expired",
        };
      }
      return null;
    } catch (err) {
      console.error("Certificate fetch error:", err);
      return null;
    }
  };

  const handleCheck = async () => {
    if (!form.unique_code.trim()) {
      setError("Please enter a batch number.");
      return;
    }
    setLoading(true);
    setError("");
    setCertificate(null);
    setVerificationFailed(false);
    setFailedResponse(null);

    try {
      const payload = { batch_number: form.unique_code.trim() };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();

      const res = await fetch(
        "https://evercorenutrition.com/ev_admin/api/authenticity-check/create/",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      const data = await res.json();
      const isSuccess =
        res.ok &&
        (data.success === true ||
          data.verified === true ||
          data.valid === true ||
          data.status === "success");

      if (isSuccess) {
        const cert = await fetchCertificate(form.unique_code.trim());
        if (cert) {
          setCertificate(cert);
          setOpen(true);
        } else {
          setError("Product verified but certificate details not found.");
        }
      } else {
        const errorMsg =
          data.error ||
          data.message ||
          data.detail ||
          "Verification failed. Please check your batch number.";
        setError(errorMsg);
        setVerificationFailed(true);
        setFailedResponse(data);
      }
    } catch (err) {
      setError(
        "Unable to connect. Please check your internet connection and try again.",
      );
      setVerificationFailed(true);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "16px",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontWeight: "600",
    fontSize: "14px",
  };

  return (
    <>
      <section style={{ padding: "60px 0", backgroundColor: "#f8f9fa" }}>
        <div
          style={{ maxWidth: "1200px", margin: "0 auto", padding: "0 16px" }}
        >
          {/* Title above flex row */}
          <div style={{ marginBottom: "32px" }}>
            <h2
              style={{
                fontSize: "clamp(28px, 5vw, 40px)",
                fontWeight: "bold",
                marginBottom: "8px",
                color: "#1a1a1a",
              }}
            >
              How to Authenticate?
            </h2>
            <span className="title-leaf">
              <svg
                className="icon-width"
                width="40"
                height="40"
                style={{ background: "transparent" }}
              >
                <use href="../assets/svg/leaf.svg#leaf"></use>
              </svg>
            </span>
          </div>

          {/* Flex row: image LEFT, form RIGHT */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "40px",
              alignItems: "flex-start",
            }}
          >
            {/* ===== LEFT - Image (Hidden on mobile) ===== */}
            <div
              style={{
                flex: "1",
                minWidth: 0,
                display: "block", // Show on desktop
              }}
              className="hide-on-mobile"
            >
              <img
                src="/assets/images/ll.png"
                alt="How to Authenticate Evercore Protein Product"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  borderRadius: "16px",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                }}
              />
            </div>

            {/* ===== RIGHT - Form (Takes full width on mobile) ===== */}
            <div
              style={{
                flex: "1",
                minWidth: 0,
                width: "100%",
              }}
            >
              <div
                style={{
                  backgroundColor: "#ffffff",
                  boxShadow: "0 10px 35px rgba(0,0,0,0.08)",
                  border: "1px solid #e9ecef",
                  borderRadius: "16px",
                  padding: "clamp(16px, 4vw, 32px)",
                }}
              >
                <h2
                  style={{
                    marginBottom: "24px",
                    fontWeight: "bold",
                    fontSize: "clamp(24px, 4vw, 32px)",
                    color: "#1a1a1a",
                  }}
                >
                  Check Authenticity
                </h2>

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                  }}
                >
                  {/* Batch Number */}
                  <div>
                    <label style={labelStyle}>
                      Batch Number <span style={{ color: "#dc2626" }}>*</span>
                    </label>
                    <input
                      type="text"
                      name="unique_code"
                      placeholder="Enter Batch Number (e.g. NELCCBB-001)"
                      value={form.unique_code}
                      onChange={handleChange}
                      style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#2c3e2f")}
                      onBlur={(e) => (e.target.style.borderColor = "#ddd")}
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label style={labelStyle}>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email address"
                      value={form.email}
                      onChange={handleChange}
                      style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#2c3e2f")}
                      onBlur={(e) => (e.target.style.borderColor = "#ddd")}
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label style={labelStyle}>Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Enter your phone number"
                      maxLength="10"
                      value={form.phone}
                      onChange={handleChange}
                      style={inputStyle}
                      onFocus={(e) => (e.target.style.borderColor = "#2c3e2f")}
                      onBlur={(e) => (e.target.style.borderColor = "#ddd")}
                    />
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div
                    style={{
                      marginTop: "20px",
                      padding: "12px",
                      backgroundColor: "#fee2e2",
                      border: "1px solid #fecaca",
                      borderRadius: "8px",
                      color: "#dc2626",
                      fontSize: "14px",
                    }}
                  >
                    <strong>Verification Failed:</strong> {error}
                  </div>
                )}

                {/* Submit Button */}
                <button
                  onClick={handleCheck}
                  disabled={loading}
                  style={{
                    width: "100%",
                    padding: "14px 0",
                    marginTop: "24px",
                    backgroundColor: "#2c3e2f",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: "bold",
                    fontSize: "16px",
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.7 : 1,
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) e.target.style.backgroundColor = "#1f2c21";
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) e.target.style.backgroundColor = "#2c3e2f";
                  }}
                >
                  {loading ? "Checking Authenticity..." : "Check Now"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CERTIFICATE POPUP ===== */}
      {open && certificate && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            padding: "20px",
          }}
          onClick={() => setOpen(false)}
        >
          <div
            style={{
              background: "#fff",
              width: "100%",
              maxWidth: "820px",
              maxHeight: "92vh",
              overflowY: "auto",
              borderRadius: "12px",
              boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "30px" }}>
              {/* Popup Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingBottom: "15px",
                  borderBottom: "1px solid #eee",
                  marginBottom: "20px",
                }}
              >
                <div>
                  <h3 style={{ margin: 0 }}>Authenticity Certificate</h3>
                  <p style={{ color: "#666", margin: "5px 0 0" }}>
                    {certificate.product_name}
                  </p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "28px",
                    cursor: "pointer",
                    color: "#999",
                  }}
                >
                  ×
                </button>
              </div>

              {/* Certificate Image */}
              {certificate.image_url && (
                <div style={{ textAlign: "center", marginBottom: "20px" }}>
                  {certificate.image_url.endsWith(".pdf") ? (
                    <a
                      href={certificate.image_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "8px 16px",
                        background: "#f8f9fa",
                        borderRadius: "6px",
                        color: "#e74c3c",
                        textDecoration: "none",
                        fontSize: "13px",
                        fontWeight: "500",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#e74c3c";
                        e.currentTarget.style.color = "white";
                        const svgPath = e.currentTarget.querySelector(
                          "svg path:first-child",
                        );
                        if (svgPath) svgPath.setAttribute("fill", "white");
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#f8f9fa";
                        e.currentTarget.style.color = "#e74c3c";
                        const svgPath = e.currentTarget.querySelector(
                          "svg path:first-child",
                        );
                        if (svgPath) svgPath.setAttribute("fill", "#e74c3c");
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z"
                          fill="#e74c3c"
                          stroke="#e74c3c"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M14 2V8H20"
                          fill="none"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 13H16M8 17H13"
                          stroke="white"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                        />
                      </svg>
                      View Certificate (PDF)
                    </a>
                  ) : (
                    <img
                      src={
                        certificate.image_url.startsWith("http")
                          ? certificate.image_url
                          : `https://evercorenutrition.com${certificate.image_url}`
                      }
                      alt="Certificate"
                      style={{
                        maxWidth: "100%",
                        maxHeight: "200px",
                        borderRadius: "6px",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        window.open(
                          certificate.image_url.startsWith("http")
                            ? certificate.image_url
                            : `https://evercorenutrition.com${certificate.image_url}`,
                          "_blank",
                        )
                      }
                    />
                  )}
                </div>
              )}
              {/* Product Details Table */}
              <div style={{ marginBottom: "25px" }}>
                <h4 style={{ marginBottom: "15px", fontSize: "18px" }}>
                  Product Details
                </h4>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    <tr>
                      <th
                        style={{
                          width: "180px",
                          padding: "10px 0",
                          textAlign: "left",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        Batch Number
                      </th>
                      <td
                        style={{
                          padding: "10px 0",
                          borderBottom: "1px solid #eee",
                          fontWeight: "600",
                        }}
                      >
                        {certificate.batch_number}
                      </td>
                    </tr>
                    <tr>
                      <th
                        style={{
                          padding: "10px 0",
                          textAlign: "left",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        Product Name
                      </th>
                      <td
                        style={{
                          padding: "10px 0",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {certificate.product_name}
                      </td>
                    </tr>
                    <tr>
                      <th
                        style={{
                          padding: "10px 0",
                          textAlign: "left",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        Variant
                      </th>
                      <td
                        style={{
                          padding: "10px 0",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {certificate.variant_name}{" "}
                        {certificate.variant_weight &&
                          `(${certificate.variant_weight})`}
                      </td>
                    </tr>
                    <tr>
                      <th
                        style={{
                          padding: "10px 0",
                          textAlign: "left",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        Certificate Date
                      </th>
                      <td
                        style={{
                          padding: "10px 0",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        {formatDate(certificate.created_at)}
                      </td>
                    </tr>
                    <tr>
                      <th
                        style={{
                          padding: "10px 0",
                          textAlign: "left",
                          borderBottom: "1px solid #eee",
                        }}
                      >
                        Status
                      </th>
                      <td
                        style={{
                          padding: "10px 0",
                          borderBottom: "1px solid #eee",
                          color: certificate.is_active ? "#16a34a" : "#dc2626",
                          fontWeight: "600",
                        }}
                      >
                        {certificate.is_active
                          ? "✓ Authentic & Active"
                          : "Inactive"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Status Banner */}
              <div
                style={{
                  padding: "16px",
                  backgroundColor: certificate.is_active
                    ? "#f0fdf4"
                    : "#fef2f2",
                  borderRadius: "8px",
                  border: `1px solid ${certificate.is_active ? "#86efac" : "#fecaca"}`,
                }}
              >
                <strong
                  style={{
                    color: certificate.is_active ? "#166534" : "#991b1b",
                  }}
                >
                  {certificate.is_active
                    ? "✓ This product is 100% Authentic"
                    : "Certificate is not active"}
                </strong>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for hiding image on mobile */}
      <style>{`
  @media (max-width: 768px) {
    .hide-on-mobile {
      display: none !important;
    }

    /* Fix section padding on mobile */
    .auth-section {
      padding: 30px 0 !important;
    }

    /* Fix form taking full width on mobile */
    .auth-flex-row {
      flex-direction: column !important;
    }

    /* Fix input font size to prevent zoom on iOS */
    .auth-section input {
      font-size: 16px !important;
    }
  }
`}</style>
    </>
  );
};

export default How_to_Authenticate;
